import { useState, useEffect, useRef } from 'react';
import {
  validatePAN,
  validateGST,
  validateMobile,
  validateEmail,
  validateIFSC,
  validateAccountNumber
} from '../../../../utils/validationUtils';
import { useDepartmentOptions } from '../../../../actions/fetchDepartmentListAction';
import { fetchPincodeDetailsAction } from '../../../../actions/pincodeAction';

/**
 * Validation function for individual fields
 */
const validateField = (fieldName, value) => {
  if (!value || value.trim() === '') {
    return null; // Don't validate empty fields in edit mode
  }

  switch (fieldName) {
    case 'panNumber':
      return validatePAN(value) === true ? null : validatePAN(value);
    case 'gstNumber':
    case 'gstNumberPrimary':
      return validateGST(value) === true ? null : validateGST(value);
    case 'mobileNumber':
    case 'contactPersonMobile':
      return validateMobile(value) === true ? null : validateMobile(value);
    case 'emailAddress':
    case 'contactPersonEmail':
      return validateEmail(value) === true ? null : validateEmail(value);
    case 'ifscCode':
      return validateIFSC(value) === true ? null : validateIFSC(value);
    case 'accountNumber':
      return validateAccountNumber(value) === true ? null : validateAccountNumber(value);
    default:
      return null;
  }
};

// Pure helper — resolves DB value (name, id number, or id string) → dropdown id string
const normalizeDepartmentValue = (value, options) => {
  if (value == null || value === '') return "";
  const raw = String(value).trim();
  if (!raw) return "";
  // Already matches an option value (id string like "5")
  if (options.some(o => o.value === raw)) return raw;
  // Try matching by label (name) — case-insensitive
  const byName = options.find(o => o.label.toLowerCase() === raw.toLowerCase());
  return byName ? byName.value : "";
};

export const useEditDetails = (clientData, onSave, onClose) => {
  const [formData, setFormData] = useState({});
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Shared hook — reads from module-level cache, no extra network call
  const departmentOptions = useDepartmentOptions();

  // Populate form when clientData changes
  useEffect(() => {
    if (clientData?.entity) {
      // DB may return aaDepartmentFunctionId (number) or aaDepartmentFunction (name string)
      // Try id first, fall back to name
      const rawDept = clientData.entity.aaDepartmentFunctionId ?? clientData.entity.aaDepartmentFunction;
      setFormData({
        ...clientData.entity,
        entityType: normalizeEntityType(clientData.entity.entityType),
        lobType: normalizeLOBType(clientData.entity.lobType),
        lobNationality: normalizeLOBNationality(clientData.entity.lobNationality),
        aaDepartmentFunction: normalizeDepartmentValue(rawDept, departmentOptions),
      });
    }
  }, [clientData]); // eslint-disable-line react-hooks/exhaustive-deps

  // Re-normalize when options finish loading (options may arrive after clientData)
  useEffect(() => {
    if (departmentOptions.length <= 1) return; // not loaded yet
    const rawDept = clientData?.entity?.aaDepartmentFunctionId ?? clientData?.entity?.aaDepartmentFunction;
    if (rawDept == null) return;
    const normalized = normalizeDepartmentValue(rawDept, departmentOptions);
    setFormData(prev => {
      if (normalized === prev.aaDepartmentFunction) return prev;
      return { ...prev, aaDepartmentFunction: normalized };
    });
  }, [departmentOptions]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-fill city & state when pinCode reaches 6 digits
  const pincodeRef = useRef(null);
  useEffect(() => {
    const pin = formData.pinCode ? String(formData.pinCode).replace(/\D/g, '') : '';
    if (pin.length !== 6) return;
    if (pincodeRef.current === pin) return; // already fetched for this pin
    pincodeRef.current = pin;
    fetchPincodeDetailsAction(pin).then(result => {
      if (result.success) {
        setFormData(prev => ({ ...prev, city: result.city, state: result.state }));
      }
    });
  }, [formData.pinCode]); // eslint-disable-line react-hooks/exhaustive-deps

  // Helper functions to normalize values to match form options
  const normalizeEntityType = (value) => {
    if (!value) return "";
    const normalized = value.toLowerCase().trim();

    // Map API values to form option values
    const mapping = {
      'proprietorship': 'proprietorship',
      'partnership': 'partnership',
      'llp': 'llp',
      'private_limited': 'private_limited',
      'public_limited': 'public_limited',
      'trust': 'trust',
      'society': 'society',
      // If API returns "client" or "vendor", it's not the entity type, return empty
      'client': '',
      'vendor': ''
    };
    return mapping[normalized] || value;
  };

  const normalizeLOBType = (value) => {
    if (!value) return "";
    const normalized = value.toLowerCase().trim();
    const mapping = {
      'travel': 'travel',
      'company': 'company',
      'auto': 'auto',
      'gadget': 'gadget',
      'non-travel': 'company',
      'nontravel': 'company',
    };
    return mapping[normalized] || value;
  };

  const normalizeLOBNationality = (value) => {
    if (!value) return "";
    const normalized = value.toLowerCase().trim();
    const mapping = {
      'domestic': 'Domestic',
      'international': 'international',
    };
    return mapping[normalized] || value;
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    let newValue = type === 'checkbox' ? checked : value;

    // Auto-uppercase for specific fields
    const uppercaseFields = ['panNumber', 'companyPAN', 'gstNumber', 'ifscCode'];
    if (uppercaseFields.includes(name) && typeof newValue === 'string') {
      newValue = newValue.toUpperCase();
    }

    // Only allow numbers for mobile, account number, and pinCode fields
    if ((name === 'mobileNumber' || name === 'accountNumber' || name === 'pinCode') && typeof newValue === 'string') {
      newValue = newValue.replace(/\D/g, '').slice(0, name === 'pinCode' ? 6 : undefined);
    }

    setFormData({
      ...formData,
      [name]: newValue
    });

    // Live validation - validate as user types
    if (type !== 'checkbox') {
      const error = validateField(name, newValue);

      setErrors(prevErrors => {
        const newErrors = { ...prevErrors };
        if (error) {
          newErrors[name] = error;
        } else {
          delete newErrors[name];
        }
        return newErrors;
      });
    }
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;

    // Additional validation on blur
    const error = validateField(name, value);

    setErrors(prevErrors => {
      const newErrors = { ...prevErrors };
      if (error) {
        newErrors[name] = error;
      } else {
        delete newErrors[name];
      }
      return newErrors;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await onSave(formData);
      // onSave (handleSaveClient) handles success toast and throws on failure
      onClose();
    } catch {
      // error toast already shown by onSave
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setFormData({});
    setErrors({});
    onClose();
  };

  const getDocumentStatusBadge = (isUploaded) => {
    if (isUploaded) {
      return <span className="doc-status uploaded">✓ Uploaded</span>;
    }
    return <span className="doc-status pending">⏳ Pending</span>;
  };

  const getFieldValidationState = (fieldName) => {
    const value = formData[fieldName];
    const error = errors[fieldName];
    const hasValue = value && value.toString().trim() !== '';
    const hasValidation = ['panNumber', 'gstNumber', 'mobileNumber', 'emailAddress', 'ifscCode', 'accountNumber'].includes(fieldName);
    const isValid = hasValue && !error && hasValidation;

    return {
      error,
      isValid,
      className: error ? 'error' : isValid ? 'success' : ''
    };
  };

  return {
    formData,
    errors,
    isSubmitting,
    departmentOptions,
    handleChange,
    handleBlur,
    handleSubmit,
    handleClose,
    getDocumentStatusBadge,
    getFieldValidationState,
  };
};
